import { ChevronDown, LogOut, Moon, Sun } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { getAuthPort } from '@/data/auth';
import { useAcesso } from '@/hooks/use-acesso';
import { itensDoMenuUsuario } from '@/navigation';

/**
 * Chip de conta na topbar — mesmo papel e anatomia do PAN (avatar + nome +
 * chevron; ao abrir, identidade, capabilities, itens administrativos (Homologação, Painel, Usuários, só com a capability), tema e "Sair"). Sem
 * personificação: o Compilações não tem esse conceito.
 */

// Duas iniciais (primeiro nome + último sobrenome) — "Paulo Basílio" -> "PB".
function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '?';
  if (partes.length === 1) return partes[0].charAt(0).toUpperCase();
  return (partes[0].charAt(0) + partes[partes.length - 1].charAt(0)).toUpperCase();
}

const ROTULOS_ROLE: Record<string, string> = {
  admin: 'Administrador',
  maintainer: 'Mantenedor',
  painel: 'Painel do cliente',
  sysadmin: 'Sysadmin',
};

interface UserChipProps {
  usuario: { nome: string; detalhe: string };
  roles: string[];
  escuro: boolean;
  aoAlternarTema: () => void;
}

export function UserChip({ usuario, roles, escuro, aoAlternarTema }: UserChipProps) {
  const inicial = iniciais(usuario.nome);
  const { acesso } = useAcesso();
  const itensAdmin = itensDoMenuUsuario(roles, acesso);
  // `detalhe` normalmente é o e-mail/login — só mostra se não for repetir o nome.
  const mostrarDetalhe = usuario.detalhe && usuario.detalhe !== usuario.nome;

  return (
    <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={`Conta de ${usuario.nome}`}
            className="flex items-center gap-2 rounded-button border border-border bg-bg-surface py-1 pl-1 pr-2.5 shadow-rest transition-colors duration-fast ease-standard hover:border-border-strong hover:bg-bg-subtle"
          >
            <span
              aria-hidden="true"
              className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary font-narrow text-[12px] font-bold text-on-primary"
            >
              {inicial}
            </span>
            <span className="hidden max-w-[10rem] flex-col items-start leading-tight sm:flex">
              <span className="w-full truncate text-[13px] font-bold">{usuario.nome}</span>
              <span className="w-full truncate text-micro font-normal text-on-surface-muted">{usuario.detalhe}</span>
            </span>
            <ChevronDown className="hidden size-4 shrink-0 text-icon sm:block" aria-hidden="true" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent className="min-w-[16rem]">
          <div className="flex items-center gap-2.5 px-2.5 py-2">
            <span
              aria-hidden="true"
              className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary font-narrow text-[14px] font-bold text-on-primary"
            >
              {inicial}
            </span>
            <div className="flex min-w-0 flex-col leading-tight">
              <span className="truncate text-body font-bold text-on-surface">{usuario.nome}</span>
              {mostrarDetalhe ? (
                <span className="truncate text-cell font-normal text-on-surface-muted">{usuario.detalhe}</span>
              ) : null}
            </div>
          </div>

          <div className="flex flex-wrap gap-1 px-2.5 pb-2">
            {roles.length > 0 ? (
              roles.map((role) => (
                <span
                  key={role}
                  className="rounded-chip bg-bg-subtle px-1.5 py-0.5 text-micro font-normal uppercase tracking-[0.04em] text-on-surface-muted"
                >
                  {ROTULOS_ROLE[role] ?? role}
                </span>
              ))
            ) : (
              <span className="text-micro font-normal text-on-surface-muted">Somente leitura</span>
            )}
          </div>

          <div className="my-1 h-px bg-border" aria-hidden="true" />

          {itensAdmin.length > 0 ? (
            <>
              {itensAdmin.map((item) => {
                const Icone = item.icone;
                return (
                  <DropdownMenuItem key={item.id} asChild>
                    <Link to={item.rota} className="items-start">
                      <Icone className="mt-0.5 size-4 shrink-0 text-icon" aria-hidden="true" />
                      <span className="flex min-w-0 flex-col leading-tight">
                        <span>{item.rotulo}</span>
                        <span className="text-micro font-normal text-on-surface-muted">{item.descricao}</span>
                      </span>
                    </Link>
                  </DropdownMenuItem>
                );
              })}
              <div className="my-1 h-px bg-border" aria-hidden="true" />
            </>
          ) : null}

          <DropdownMenuItem onSelect={aoAlternarTema}>
            {escuro ? (
              <Sun className="size-4 shrink-0 text-icon" aria-hidden="true" />
            ) : (
              <Moon className="size-4 shrink-0 text-icon" aria-hidden="true" />
            )}
            {escuro ? 'Usar tema claro' : 'Usar tema escuro'}
          </DropdownMenuItem>

          <div className="my-1 h-px bg-border" aria-hidden="true" />

          <DropdownMenuItem onSelect={() => getAuthPort().logout()} className="text-danger">
            <LogOut className="size-4 shrink-0" aria-hidden="true" />
            Sair
          </DropdownMenuItem>
        </DropdownMenuContent>
    </DropdownMenu>
  );
}
